import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { z } from 'zod'
import {
  Channel,
  ChannelHealth,
  DisconnectChannelResponse,
  StartOAuthResponse,
  type Platform,
  type StartOAuthRequest,
} from '@rp/contracts'
import { nullWhenAbsent } from '../../api/absent'
import { useReadySession } from '../../session/useSession'
import { settingsKeys } from './queries'

export const useChannels = () => {
  const { api, membership } = useReadySession()
  return useQuery({
    queryKey: settingsKeys.channels(membership.tenantId),
    queryFn: async () => (await api.request(z.array(Channel), { method: 'GET', path: '/v1/channels' })).data,
  })
}

export const CHANNEL_HEALTH_REFRESH_MS = 5 * 60 * 1000

export const useChannelHealth = () => {
  const { api, membership } = useReadySession()
  return useQuery({
    queryKey: settingsKeys.channelHealth(membership.tenantId),
    queryFn: async () =>
      nullWhenAbsent(
        api.request(z.array(ChannelHealth), { method: 'GET', path: '/v1/channels/health' }).then((result) => result.data),
      ),
    refetchInterval: CHANNEL_HEALTH_REFRESH_MS,
  })
}

export const useStartConnect = () => {
  const { api } = useReadySession()
  return useMutation({
    mutationFn: async (platform: Platform) =>
      (
        await api.request(StartOAuthResponse, {
          method: 'POST',
          path: '/v1/channels/oauth/start',
          body: { platform } satisfies StartOAuthRequest,
        })
      ).data,
    onSuccess: (started) => window.location.assign(started.url),
  })
}

export const useDisconnectChannel = () => {
  const { api, membership } = useReadySession()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (channel: Channel) =>
      (await api.request(DisconnectChannelResponse, { method: 'DELETE', path: `/v1/channels/${channel.id}` })).data,
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: settingsKeys.channels(membership.tenantId) })
      void queryClient.invalidateQueries({ queryKey: settingsKeys.channelHealth(membership.tenantId) })
    },
  })
}
